/**
 * The schema registry: master types, their versions, and submitting one
 * for review.
 *
 * Activation is deliberately absent, because the API has no route for it.
 * A version reaches `active` only through the approval flow — there is no
 * way to skip the reviewer, and no way for this file to offer one.
 */

import { client } from "@/lib/api/client";
import type { components } from "@/lib/api/schema";

export type SchemaVersion = components["schemas"]["SchemaVersionOut"];
export type SchemaVersionPage = components["schemas"]["SchemaVersionPage"];
export type MasterType = components["schemas"]["MasterTypeOut"];
export type MasterTypeCreate = components["schemas"]["MasterTypeCreate"];
export type Submission = components["schemas"]["SubmissionOut"];

function schemas(code: string): string {
  return `/api/v1/master-types/${encodeURIComponent(code)}/schemas`;
}

export function getMasterType(code: string, locale?: string): Promise<MasterType> {
  return client.request<MasterType>(`/api/v1/master-types/${encodeURIComponent(code)}`, {
    query: { locale },
  });
}

export function createMasterType(body: MasterTypeCreate): Promise<MasterType> {
  return client.request<MasterType>("/api/v1/master-types", { method: "POST", body });
}

export function listSchemaVersions(
  code: string,
  params: { cursor?: string | undefined; limit?: number | undefined } = {},
): Promise<SchemaVersionPage> {
  return client.request<SchemaVersionPage>(schemas(code), {
    query: { cursor: params.cursor, limit: params.limit },
  });
}

export function getSchemaVersion(code: string, version: number): Promise<SchemaVersion> {
  return client.request<SchemaVersion>(`${schemas(code)}/${version}`);
}

export function createSchemaVersion(
  code: string,
  definition: Record<string, unknown>,
): Promise<SchemaVersion> {
  return client.request<SchemaVersion>(schemas(code), { method: "POST", body: { definition } });
}

export function submitSchemaVersion(schemaId: string): Promise<Submission> {
  return client.request<Submission>("/api/v1/schema-submissions", {
    method: "POST",
    body: { master_type_schema_id: schemaId },
  });
}

/**
 * The version a data-entry form is compiled from.
 *
 * Its own route rather than a filter over `listSchemaVersions`: that list
 * is ordered oldest first and paginated, so scanning it for
 * `status === "active"` is correct for a type with three versions and
 * silently wrong for one with sixty.
 *
 * 404s when the type has no active version yet — which is a real state,
 * not an error, and the form route renders it as "this type has no
 * schema yet" rather than as a failure.
 */
export function getActiveSchemaVersion(code: string): Promise<SchemaVersion> {
  return client.request<SchemaVersion>(`${schemas(code)}/active`);
}
